import React, { useState, useLayoutEffect } from "react";
import { GateScreen } from "./GateScreen";
import { T } from "../lib/theme";

// cradlehq.net is a placeholder — update once the parent site has a
// permanent URL for the footer's "part of the CradleHQ portfolio" link.
const CRADLEHQ_URL = "https://cradlehq.net";

// Shared "eyebrow" style for a small section disclaimer — module-level
// since both HomeFleetLanding and the tab-content components below need it.
const sectionLabel = {fontSize:11,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10};

// Static illustrative example — matches the two-message shape of a real
// Kate/Clyde exchange, but hardcoded copy, no state, no API calls.
function ChatExchange({name, userMsg, replyMsg}){
  const bubbleBase={borderRadius:12,padding:"9px 13px",fontSize:13,lineHeight:1.5,maxWidth:"88%"};
  return(
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <div style={{...bubbleBase,alignSelf:"flex-end",background:T.slateL,color:T.text,border:`1px solid ${T.border}`}}>
        {userMsg}
      </div>
      <div style={{...bubbleBase,alignSelf:"flex-start",background:T.tealSoftBg,color:T.text,border:`1px solid ${T.tealBorder}`}}>
        <span style={{color:T.teal,fontWeight:700,marginRight:6}}>{name}:</span>{replyMsg}
      </div>
    </div>
  );
}

// Illustrative mockups only — not real screenshots, no live data. Built with
// plain inline styles rather than the app's .hf-vcard/.hf-fin-strip classes
// because those only exist once HomeFleetApp.jsx injects getStyles(T), which
// never mounts for an unauthenticated visitor on this page.
function VehicleCardMockup(){
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
      <div style={{width:40,height:40,borderRadius:10,background:T.slateL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>🚙</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text}}>2019 Chevrolet Tahoe</div>
        <div style={{fontSize:12,color:T.textMd}}>62,400 mi · Est. value $25,600</div>
      </div>
      <div style={{fontSize:11,fontWeight:700,color:T.teal,background:T.tealL,borderRadius:6,padding:"3px 8px",whiteSpace:"nowrap",flexShrink:0}}>On track</div>
    </div>
  );
}

// One detail row inside an expanded accordion tile or mini panel —
// label/value pair, matching the real app's row shape. "last" drops the
// dashed divider for the row right before a bold total (or the final row
// in a panel with no total).
function DetailRow({label, value, tone, bold, badge, last}){
  const valueColor = tone==="green"?T.green:tone==="red"?T.red:T.text;
  return(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",fontSize:12.5,borderBottom:last?"none":`1px dashed ${T.border}`}}>
      <div style={{color:bold?T.text:T.textMd,fontWeight:bold?700:500,display:"flex",alignItems:"center",gap:6}}>
        {label}
        {badge&&<span style={{fontSize:9,fontWeight:700,color:T.amber,background:T.amberL,borderRadius:5,padding:"1px 5px"}}>{badge}</span>}
      </div>
      <div style={{color:valueColor,fontWeight:bold?800:600,fontSize:bold?13.5:12.5}}>{value}</div>
    </div>
  );
}

function TogglePill({label, active}){
  return(
    <div style={{flex:1,textAlign:"center",fontSize:11.5,fontWeight:700,padding:"6px 0",borderRadius:8,background:active?T.tealL:T.slateL,color:active?T.teal:T.textMd,border:`1px solid ${active?T.tealBorderStrong:T.border}`}}>
      {label}
    </div>
  );
}

function TileTotal({label, value, color}){
  return(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:8,marginTop:4,borderTop:`1px solid ${T.border}`}}>
      <div style={{fontWeight:800,fontSize:13,color:T.text}}>{label}</div>
      <div style={{fontWeight:800,fontSize:15,color:color||T.text}}>{value}</div>
    </div>
  );
}

// One accordion tile — icon+label header with a headline value. Owns its
// own open/closed state (defaultOpen sets the initial value only) so
// DealBuilderMockup can open Price by default while collapsing Trade and
// Financing, each independently toggleable by clicking its header.
function AccordionTile({icon, label, headline, headlineColor, children, defaultOpen=true}){
  const[open,setOpen]=useState(defaultOpen);
  const toggle=()=>setOpen(o=>!o);
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 16px",marginBottom:10}}>
      <div
        role="button" tabIndex={0}
        onClick={toggle}
        onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();toggle();}}}
        style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:open?10:0,cursor:"pointer"}}>
        <div style={{fontSize:13.5,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:8}}>
          <span>{icon}</span>{label}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14,fontWeight:800,color:headlineColor||T.text}}>{headline}</span>
          {!open&&<span style={{fontSize:10.5,fontWeight:700,color:T.teal}}>Tap to expand</span>}
          <span style={{fontSize:10,color:T.textSm,display:"inline-block",transition:"transform .15s",transform:open?"rotate(0deg)":"rotate(-90deg)"}}>▾</span>
        </div>
      </div>
      {open&&children}
    </div>
  );
}

// A closer illustrative look at Clyde's real deal-builder worksheet
// (BuyingModal's tiles in HomeFleet_BuyingFlow.jsx) and how live incentive
// research surfaces inside the Price tile — the APR-vs-Cash-Back toggle
// plus stacked conquest/regional/loyalty bonus rows. Hardcoded numbers, no
// state beyond each tile's own open/closed toggle, no API calls. Price
// stays open by default; Trade and Financing start collapsed.
function DealBuilderMockup(){
  return(
    <div>
      <AccordionTile icon="🏷️" label="Price" headline="$27,030">
        <DetailRow label="Retail value" value="$28,450"/>
        <DetailRow label="Dealer price" value="$27,100"/>
        <DetailRow label="Discount" value="-$1,350" tone="green"/>
        <div style={{display:"flex",gap:8,margin:"10px 0"}}>
          <TogglePill label="APR Offer" active={false}/>
          <TogglePill label="Cash Back" active={true}/>
        </div>
        <DetailRow label="✓ Conquest bonus" value="-$500" tone="green"/>
        <DetailRow label="✓ Loyalty bonus" value="-$750" tone="green"/>
        <DetailRow label="Incentives total" value="-$1,250" tone="green"/>
        <DetailRow label="Est. TTL" value="$1,890" badge="estimated"/>
        <DetailRow label="Tax + fees" value="$640" last/>
        <TileTotal label="Net price" value="$27,030"/>
      </AccordionTile>

      <AccordionTile icon="🔄" label="Trade" headline="$5,100" headlineColor={T.green} defaultOpen={false}>
        <div style={{fontSize:12,color:T.textMd,marginBottom:6}}>2019 Chevrolet Tahoe</div>
        <DetailRow label="Down payment" value="$2,000"/>
        <DetailRow label="Trade allowance" value="$9,200"/>
        <DetailRow label="Loan payoff" value="-$4,100" tone="red" last/>
        <TileTotal label="Net equity" value="$5,100" color={T.green}/>
      </AccordionTile>

      <AccordionTile icon="💳" label="Financing" headline="$412/mo" defaultOpen={false}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:22,fontWeight:700,color:T.text,margin:"2px 0 10px"}}>$412/mo</div>
        <DetailRow label="Total interest" value="$3,240"/>
        <DetailRow label="Total cost" value="$31,510" last/>
        <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`}}>
          <DetailRow label="Loan payment" value="$412"/>
          <DetailRow label="Est. insurance" value="$145"/>
          <DetailRow label="Est. fuel" value="$180" last/>
          <TileTotal label="Total monthly" value="$737"/>
        </div>
        <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6}}>
          <div style={{fontSize:11.5,fontWeight:700,color:T.green,background:T.greenL,borderRadius:8,padding:"6px 10px"}}>
            ▾ $58/mo less than your current payment
          </div>
          <div style={{fontSize:11.5,fontWeight:700,color:T.green,background:T.greenL,borderRadius:8,padding:"6px 10px"}}>
            ▾ $3,480 less over the life of the loan
          </div>
        </div>
      </AccordionTile>
    </div>
  );
}

// The five real VehicleRow.jsx tabs (Financial / Safety & Recalls / Fuel
// Economy / Dates & Coverage / Maintenance), single-open — same shape as
// VehicleRow's own activeTab state, just illustrative fake numbers.
const MINI_SECTIONS = [
  {key:"financial", label:"Financial"},
  {key:"safety", label:"Safety & Recalls"},
  {key:"fuel", label:"Fuel Economy"},
  {key:"dates", label:"Dates & Coverage"},
  {key:"maint", label:"Maintenance"},
];

// One cell of the mocked finance strip — mirrors VehicleRow.jsx's real
// hf-fin-cell (Insurance/Refinance/Sell Offer), which highlights with a
// teal-tinted card whenever there's a savings/offer number to show, same
// treatment used here since all three example values are "good news".
function FinanceStripCell({label, value, valueColor}){
  return(
    <div style={{background:T.tealSoftBg,border:`1px solid ${T.tealBorder}`,borderRadius:9,padding:"8px 6px",textAlign:"center"}}>
      <div style={{fontSize:9.5,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:3}}>{label}</div>
      <div style={{fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:800,color:valueColor}}>{value}</div>
    </div>
  );
}

function MiniPanel({children, disclaimer}){
  return(
    <div style={{background:T.slateL,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
      {children}
      {disclaimer&&<div style={{fontSize:10.5,color:T.textSm,marginTop:8,fontStyle:"italic"}}>Illustrative example — not live NHTSA/EPA data</div>}
    </div>
  );
}

function PlatformPreview(){
  const[activeTab,setActiveTab]=useState("financial");
  return(
    <div>
      <VehicleCardMockup/>

      <div style={{marginTop:12}}>
        <div style={{fontSize:11,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Savings available</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:7}}>
          <FinanceStripCell label="Insurance" value="+$65/mo" valueColor={T.green}/>
          <FinanceStripCell label="Refinance" value="+$15/mo" valueColor={T.green}/>
          <FinanceStripCell label="Sell Offer" value="$42,900" valueColor={T.teal}/>
        </div>
        <div style={{fontSize:10,color:T.textSm,marginTop:6,lineHeight:1.5}}>
          Areas without a live savings partner or a real sell-offer will show simulated values based on researched data — not yet actionable.
        </div>
      </div>

      <div style={{display:"flex",gap:6,margin:"12px 0",flexWrap:"wrap"}}>
        {MINI_SECTIONS.map(s=>(
          <button key={s.key} type="button" onClick={()=>setActiveTab(t=>t===s.key?null:s.key)}
            style={{fontSize:11,fontWeight:700,padding:"6px 10px",borderRadius:8,cursor:"pointer",
              background:activeTab===s.key?T.tealL:T.slateL,
              color:activeTab===s.key?T.teal:T.textMd,
              border:`1px solid ${activeTab===s.key?T.tealBorderStrong:T.border}`}}>
            {s.label}
          </button>
        ))}
      </div>
      {activeTab==="financial"&&(
        <>
          <MiniPanel>
            <DetailRow label="Market Value" value="$25,600"/>
            <DetailRow label="Loan Balance" value="$9,800"/>
            <DetailRow label="Equity" value="$15,800" tone="green"/>
            <DetailRow label="Monthly Pmt" value="$310"/>
            <DetailRow label="Interest Rate" value="4.9%"/>
            <DetailRow label="Insurance/mo" value="$145" last/>
          </MiniPanel>
          <div style={{marginTop:10}}>
            <div style={sectionLabel}>Monthly Cost of Ownership</div>
            <div style={{background:T.slateL,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>
                <span style={{fontSize:13,fontWeight:700,color:T.text}}>Total</span>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:18,fontWeight:800,color:T.teal}}>$585/mo</span>
              </div>
              <DetailRow label="Loan payment" value="$310"/>
              <DetailRow label="Est. insurance" value="$145"/>
              <DetailRow label="Est. fuel" value="$95"/>
              <DetailRow label="Est. maintenance" value="$35" last/>
            </div>
          </div>
        </>
      )}
      {activeTab==="safety"&&(
        <MiniPanel disclaimer>
          <DetailRow label="Overall Safety Rating" value="5 / 5 stars"/>
          <DetailRow label="Open Recalls" value="0 open" tone="green" last/>
        </MiniPanel>
      )}
      {activeTab==="fuel"&&(
        <MiniPanel disclaimer>
          <DetailRow label="City" value="19 mpg"/>
          <DetailRow label="Highway" value="25 mpg"/>
          <DetailRow label="Combined" value="21 mpg"/>
          <DetailRow label="Cost per Mile" value="$0.16" last/>
        </MiniPanel>
      )}
      {activeTab==="dates"&&(
        <MiniPanel>
          <DetailRow label="Registration Expires" value="Mar 2027 · 542 days left" tone="green"/>
          <DetailRow label="Warranty Ends" value="Jan 2025 · Expired" tone="red" last/>
        </MiniPanel>
      )}
      {activeTab==="maint"&&(
        <MiniPanel>
          <DetailRow label="Oil change" value="Due in 1,200 mi"/>
          <DetailRow label="Tire rotation" value="Up to date" tone="green" last/>
        </MiniPanel>
      )}
    </div>
  );
}

function CloserLookToggle({open, onClick}){
  return(
    <button type="button" onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",border:"none",color:T.teal,fontWeight:700,fontSize:13,cursor:"pointer",padding:"4px 0",marginTop:6}}>
      {open?"Hide the closer look":"Click here for a closer look"}
      <span style={{display:"inline-block",transition:"transform .15s",transform:open?"rotate(180deg)":"none",fontSize:11}}>▾</span>
    </button>
  );
}

function PlatformTabContent(){
  const[open,setOpen]=useState(false);
  return(
    <div>
      <p style={{fontSize:14.5,color:T.textMd,lineHeight:1.65,margin:"0 0 12px"}}>
        See everything about your vehicles in one place: complete ownership cost breakdowns, safety
        ratings and recall awareness, savings opportunities on insurance and financing, and automatic
        reminders for maintenance, warranty expiration, and registration renewals. HomeFleet even
        breaks down your fuel cost down to the mile.
      </p>
      <CloserLookToggle open={open} onClick={()=>setOpen(o=>!o)}/>
      {open&&(
        <div style={{marginTop:14}}>
          <div style={sectionLabel}>Illustrative example — not a live account</div>
          <PlatformPreview/>
        </div>
      )}
    </div>
  );
}

function KateClydeTabContent(){
  const[open,setOpen]=useState(false);
  return(
    <div>
      <p style={{fontSize:14.5,color:T.textMd,lineHeight:1.65,margin:"0 0 12px"}}>
        Kate is your personal AI assistant — she helps you navigate the platform, gives
        recommendations, and walks you through vehicle issues and scheduling. She can even help you
        research and identify your next vehicle, then hands you off to Clyde. Clyde digs deeper: real
        research on factory incentives, deal structures he analyzes and advises on, and multi-dealer
        outreach that lets dealers compete for your business.
      </p>
      <div style={{display:"flex",flexDirection:"column",gap:14,margin:"14px 0"}}>
        <ChatExchange
          name="Kate"
          userMsg="When's my Civic's next oil change due?"
          replyMsg="You're due at 47,500 miles — about 3 weeks out at your driving pace. Want me to find nearby service centers?"
        />
        <ChatExchange
          name="Clyde"
          userMsg="What's my 2019 Tahoe worth right now?"
          replyMsg="Checking live market data — your Tahoe's trade-in range is $24,800–$26,400 based on current mileage and local listings. Want me to pull dealer offers?"
        />
      </div>
      <CloserLookToggle open={open} onClick={()=>setOpen(o=>!o)}/>
      {open&&(
        <div style={{marginTop:14}}>
          <div style={sectionLabel}>Illustrative example — deal builder & incentive research</div>
          <DealBuilderMockup/>
        </div>
      )}
    </div>
  );
}

const SUPER_TABS = [
  {key:"platform", label:"The Platform"},
  {key:"kate", label:"Meet Kate & Clyde"},
];

function SuperTabs({active, onChange}){
  return(
    <div style={{display:"flex",borderBottom:`1px solid ${T.border}`}}>
      {SUPER_TABS.map(t=>(
        <button key={t.key} type="button" onClick={()=>onChange(t.key)}
          style={{flex:1,background:"none",border:"none",borderBottom:`2px solid ${active===t.key?T.teal:"transparent"}`,
            color:active===t.key?T.teal:T.textMd,fontWeight:700,fontSize:14,padding:"12px 4px",cursor:"pointer",marginBottom:-1}}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Landing / marketing wrapper ─────────────────────────
   Sits in normal document flow above <GateScreen/>, which keeps its own
   full-viewport centering untouched — this component never nests it
   inside another min-height:100vh flex container. GateScreen itself is
   unmodified; the "request access" line lives here since GateScreen has
   no slot for it. */
export function HomeFleetLanding({onUnlock}){
  // GateScreen's email input autoFocuses, which makes the browser jump the
  // viewport straight to it on load — before this wrapper existed that was
  // fine (it was the only thing on the page), but now it scrolls the
  // header/hero content above it out of view. Runs after React's commit
  // (when the native autofocus scroll has already fired) but before paint,
  // so it overrides that scroll with no visible flash.
  useLayoutEffect(()=>{window.scrollTo(0,0);},[]);

  const[activeSuperTab,setActiveSuperTab]=useState("platform");

  const wrap={maxWidth:560,margin:"0 auto",padding:"28px 20px 48px",fontFamily:"'Plus Jakarta Sans',sans-serif",background:T.bg,color:T.text};
  const link={color:T.teal,fontWeight:600,textDecoration:"none"};
  const btnBase={border:"none",borderRadius:8,fontWeight:700,fontSize:13,padding:"9px 16px",cursor:"pointer"};

  // Sign-in already lives further down this same page (GateScreen), so
  // these scroll to it rather than navigating. Sign Up additionally
  // simulates a click on GateScreen's own internal "Sign up" toggle
  // (queried by its rendered text, since GateScreen isn't ours to add a
  // prop/className to) so a visitor lands directly in signup mode — fired
  // ~500ms after the scroll starts as a practical stand-in for "once the
  // scroll finishes" (scrollend isn't reliably supported everywhere yet).
  // Optional chaining means a not-found toggle (e.g. already in signup
  // mode) is a silent no-op, never a throw.
  const scrollToGate=()=>{document.getElementById("hf-gate")?.scrollIntoView({behavior:"smooth",block:"start"});};
  const clickGateSignUp=()=>{
    const toggle=[...document.querySelectorAll("button")].find(b=>b.textContent.trim()==="Sign up");
    toggle?.click();
  };
  const goToSignUp=()=>{scrollToGate();setTimeout(clickGateSignUp,500);};

  return(
    <div style={{background:T.bg,minHeight:"100vh"}}>
      <div style={wrap}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:36}}>
          <div style={{fontSize:16,fontWeight:800}}>HomeFleet</div>
          <div style={{display:"flex",gap:8}}>
            <button type="button" onClick={scrollToGate} style={{...btnBase,background:"none",border:`1.5px solid ${T.teal}`,color:T.teal}}>Sign In</button>
            <button type="button" onClick={goToSignUp} style={{...btnBase,background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff"}}>Sign Up</button>
          </div>
        </div>

        <div style={{marginBottom:28}}>
          <div style={{fontSize:12,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>
            HomeFleet — Household Vehicle Management
          </div>
          <div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,fontWeight:700,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 10px",marginBottom:18}}>
            <span style={{color:T.teal}}>●</span> In testing
          </div>
          <h1 style={{fontSize:30,fontWeight:800,lineHeight:1.2,margin:"0 0 14px"}}>
            The fleet manager your household never had.
          </h1>
          <p style={{fontSize:14.5,color:T.textMd,lineHeight:1.65,margin:0}}>
            Multi-vehicle households have no unified view of what they own. They overpay on
            insurance, miss refinancing windows, ignore recalls, and sell at the wrong time for
            the wrong price. HomeFleet gives a family the same clarity over its cars that a
            fleet manager has over a commercial fleet.
          </p>
        </div>

        <div style={{marginBottom:28,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
          <SuperTabs active={activeSuperTab} onChange={setActiveSuperTab}/>
          <div style={{padding:"18px 16px"}}>
            {activeSuperTab==="platform"?<PlatformTabContent/>:<KateClydeTabContent/>}
          </div>
        </div>

        <button type="button" onClick={goToSignUp} style={{display:"block",width:"100%",textAlign:"center",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"14px 0",fontSize:15,fontWeight:700,cursor:"pointer"}}>
          See your own vehicle — add your first one free
        </button>
      </div>

      {/* GateScreen's own root div sets minHeight:100vh to center itself when
          it's the only thing on the page. Now that marketing content sits
          above it, that same 100vh still applies and leaves a large empty
          gap before the (vertically centered) form comes into view. Can't
          edit GateScreen.jsx, so this overrides that one inline declaration
          from outside via a scoped !important — !important beats a
          non-important inline style regardless of specificity — swapping
          the viewport-relative min-height for real padding that hugs the
          form's actual content height instead. */}
      <style>{`.hf-gate-shrink>div{min-height:0!important;padding:56px 0!important}`}</style>
      <div id="hf-gate" className="hf-gate-shrink">
        <GateScreen onUnlock={onUnlock}/>
      </div>

      <div style={{...wrap,paddingTop:20,textAlign:"center"}}>
        <div style={{fontSize:13,marginBottom:24}}>
          Don't have access? <a href="mailto:hello@cradlehq.app" style={link}>Request it →</a>
        </div>
        <div style={{fontSize:12,color:T.textSm}}>
          HomeFleet is part of the <a href={CRADLEHQ_URL} style={{...link,fontSize:12}}>CradleHQ</a> portfolio.
        </div>
      </div>
    </div>
  );
}
